// /tools/youtube-chapter-checker — runs in the browser: why YouTube isn't showing your chapters, and a fix
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faTriangleExclamation, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import Seo, { SITE_URL } from '../site/Seo';
import CtaBand from '../site/CtaBand';
import Faq from '../site/Faq';
import { FREE_NOTE } from '../useCases/UseCasePage';
import { MoreTools } from './YouTubeToolPage';
import { checkChapters, parseStamp } from './chapterCheck';
import { toolBySlug } from './toolData';
import { track } from '../../analytics/track';
import '../useCases/useCases.css';

const SAMPLE = '0:12 Intro\n0:18 Why chapters matter\n2:05 The 3 rules\n1:40 Common mistakes\n5:30 Wrap-up';

const FAQ = [
  ['What are YouTube’s chapter rules?', 'The first timestamp must be 0:00, there must be at least 3 timestamps in order, and each chapter must be at least 10 seconds long. If any rule fails, YouTube shows no chapters at all.'],
  ['Where do chapters go?', 'In the video’s description, one per line, each line starting with its time, like 0:00 Intro.'],
  ['I fixed them but they still don’t show. Why?', 'YouTube can take a while to pick up a changed description, so check again later. If they still don’t show, paste the description here once more: a first timestamp other than 0:00 or one chapter under 10 seconds is enough to hide them all.'],
  ['Can Bumpups write the chapters for me?', 'Yes. The free YouTube timestamp generator watches the video and writes chapters that follow these rules.'],
];

export default function ChapterCheckerPage() {
  const tool = toolBySlug('youtube-chapter-checker');
  const [text, setText] = useState('');
  const [length, setLength] = useState('');
  const [copied, setCopied] = useState(false);
  const duration = parseStamp(length.trim()) || 0;
  const result = useMemo(() => (text.trim() ? checkChapters(text, duration) : null), [text, duration]);

  const copy = () => {
    navigator.clipboard?.writeText(result.fixed).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    track('tool_used', { tool: 'checker', action: 'copy_fix' });
  };

  return (
    <>
      <Seo
        title={tool.seo.title}
        description={tool.seo.description}
        jsonLd={{ '@type': 'SoftwareApplication', name: tool.label, url: `${SITE_URL}/tools/${tool.slug}`, applicationCategory: 'MultimediaApplication', operatingSystem: 'Web', offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' } }}
      />
      <section className="uc-hero mk-hero-bg">
        <div className="mk-container">
          <p className="mk-eyebrow"><FontAwesomeIcon icon={tool.icon} /> Free tool · no sign-up</p>
          <h1 className="mk-h1 uc-title">{tool.h1}</h1>
          <p className="mk-lead">{tool.lead}</p>
        </div>
      </section>

      <section className="mk-section mk-section--tight" style={{ paddingTop: 0 }}>
        <div className="mk-container">
          <div className="tl-checker">
            <div>
              <textarea
                className="tl-textarea"
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={'Paste your timestamps, one per line:\n\n0:00 Intro\n1:15 First topic\n…'}
                aria-label="Your timestamps"
                spellCheck={false}
              />
              <label className="tl-length">
                Video length (optional)
                <input value={length} onChange={e => setLength(e.target.value)} placeholder="e.g. 12:34" aria-label="Video length" />
              </label>
              {!text && (
                <button type="button" className="mk-btn mk-btn--sm" style={{ marginTop: 12 }} onClick={() => setText(SAMPLE)}>
                  Try an example
                </button>
              )}
            </div>
            <div className="tl-result" aria-live="polite">
              {!result ? (
                <p className="mk-text" style={{ marginTop: 0 }}>Your results show up here as you type.</p>
              ) : (
                <>
                  <p className={`tl-status ${result.valid ? 'is-ok' : 'is-bad'}`}>
                    <FontAwesomeIcon icon={result.valid ? faCircleCheck : faTriangleExclamation} />
                    {result.valid
                      ? `These ${result.chapters.length} chapters follow YouTube’s rules`
                      : `${result.issues.filter(i => i.level === 'error').length} problem${result.issues.filter(i => i.level === 'error').length === 1 ? '' : 's'} found`}
                  </p>
                  {result.issues.length > 0 && (
                    <ul className="tl-issues">
                      {result.issues.map((i, n) => <li key={n} className={i.level === 'warn' ? 'is-warn' : ''}>{i.message}</li>)}
                    </ul>
                  )}
                  {result.fixed && (
                    <>
                      <p className="ucd-label">{result.valid ? 'Clean copy' : 'Fixed list'}</p>
                      <pre className="tl-fixed">{result.fixed}</pre>
                      {result.fixed.split('\n').length < 3 && <p className="tl-error">Still fewer than 3 chapters: add more before pasting.</p>}
                      <button type="button" className="mk-btn mk-btn--sm tl-copy" onClick={copy}>
                        <FontAwesomeIcon icon={copied ? faCheck : faCopy} /> {copied ? 'Copied' : 'Copy'}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          <p className="mk-note" style={{ textAlign: 'center', marginTop: 28 }}>
            Need the chapters written for you? <Link className="mk-accent" to="/tools/youtube-timestamp-generator">Generate them free</Link>. {FREE_NOTE}
          </p>
        </div>
      </section>

      <Faq title="Why chapters don’t show" items={FAQ} />
      <MoreTools current={tool.slug} />
      <CtaBand />
    </>
  );
}
